import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { api } from "../api";

const PAGE_SIZE = 8;

const RECORD_TYPES = [
  "timeline_event",
  "prop_object",
  "costume",
  "physical_state",
  "vehicle",
  "document",
  "story_rule",
  "promise_obligation",
  "symbol_motif",
  "other",
];

const EMPTY_FORM = {
  recordType: "timeline_event",
  title: "",
  fact: "",
  timing: "",
  continuityRule: "",
  notes: "",
};

function clean(value) {
  return value == null
    ? ""
    : String(value).trim();
}

function typeLabel(
  value,
  isFR
) {
  const en = {
    timeline_event:
      "Timeline event",
    prop_object:
      "Prop / object",
    costume:
      "Costume",
    physical_state:
      "Physical state",
    vehicle:
      "Vehicle",
    document:
      "Document",
    story_rule:
      "Story rule",
    promise_obligation:
      "Promise / obligation",
    symbol_motif:
      "Symbol / motif",
    other:
      "Other",
  };

  const fr = {
    timeline_event:
      "Événement chronologique",
    prop_object:
      "Accessoire / objet",
    costume:
      "Costume",
    physical_state:
      "État physique",
    vehicle:
      "Véhicule",
    document:
      "Document",
    story_rule:
      "Règle de l'histoire",
    promise_obligation:
      "Promesse / obligation",
    symbol_motif:
      "Symbole / motif",
    other:
      "Autre",
  };

  return isFR
    ? fr[value] || value
    : en[value] || value;
}

function searchText(record) {
  return [
    record?.recordType,
    record?.title,
    record?.fact,
    record?.timing,
    record?.continuityRule,
    record?.notes,
    record?.status,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function Field({
  label,
  help,
  children,
}) {
  return (
    <label
      style={{
        display: "grid",
        gap: 6,
      }}
    >
      <span
        style={{
          fontWeight: 700,
          color: "#334155",
        }}
      >
        {label}
      </span>

      {help ? (
        <span
          className="ns-card-meta"
          style={{
            margin: 0,
            lineHeight: 1.4,
          }}
        >
          {help}
        </span>
      ) : null}

      {children}
    </label>
  );
}

function ContinuityRecordCard({
  record,
  isFR,
  canManage,
  onEdit,
  onArchive,
  onRestore,
}) {
  const archived =
    clean(
      record?.status
    ).toLowerCase() ===
    "archived";

  return (
    <article
      className={`screenplay-continuity-record${
        archived
          ? " screenplay-continuity-record--archived"
          : ""
      }`}
      style={{
        marginBottom: 10,
        padding: "14px 16px",
        border: archived
          ? "1px dashed #cbd5e1"
          : "1px solid #dbe7df",
        borderLeft: archived
          ? "4px solid #94a3b8"
          : "4px solid #0f766e",
        borderRadius: 12,
        background: archived
          ? "#f8fafc"
          : "#ffffff",
        boxShadow: archived
          ? "none"
          : "0 1px 2px rgba(15,23,42,0.04)",
        opacity: archived
          ? 0.82
          : 1,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            minWidth: 0,
          }}
        >
          <p
            className="ns-card-meta"
            style={{
              margin: "0 0 4px",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontWeight: 800,
              color: archived
                ? "#64748b"
                : "#0f766e",
            }}
          >
            {typeLabel(
              record?.recordType ||
                "other",
              isFR
            )}
          </p>

          <h4
            style={{
              margin: 0,
              color: "#0f172a",
            }}
          >
            {record?.title ||
              (isFR
                ? "Élément de continuité sans titre"
                : "Untitled continuity record")}
          </h4>

          {archived ? (
            <p
              className="ns-card-meta"
              style={{
                margin: "4px 0 0",
              }}
            >
              {isFR
                ? "Archivé"
                : "Archived"}
            </p>
          ) : null}
        </div>

        {canManage ? (
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              className="ns-button ns-button-outline"
              onClick={() =>
                onEdit(record)
              }
            >
              {isFR
                ? "Modifier"
                : "Edit"}
            </button>

            {archived ? (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() =>
                  onRestore(record)
                }
              >
                {isFR
                  ? "Restaurer"
                  : "Restore"}
              </button>
            ) : (
              <button
                type="button"
                className="ns-button ns-button-outline"
                onClick={() =>
                  onArchive(record)
                }
              >
                {isFR
                  ? "Archiver"
                  : "Archive"}
              </button>
            )}
          </div>
        ) : null}
      </div>

      {record?.fact ? (
        <p
          style={{
            margin: "10px 0 0",
            color: "#334155",
            lineHeight: 1.5,
          }}
        >
          <strong>
            {isFR
              ? "Fait établi"
              : "Established fact"}
            :
          </strong>{" "}
          {record.fact}
        </p>
      ) : null}

      {record?.timing ? (
        <p
          style={{
            margin: "8px 0 0",
            color: "#334155",
            lineHeight: 1.5,
          }}
        >
          <strong>
            {isFR
              ? "Moment / séquence"
              : "Timing / sequence"}
            :
          </strong>{" "}
          {record.timing}
        </p>
      ) : null}

      {record?.continuityRule ? (
        <p
          style={{
            margin: "8px 0 0",
            color: "#334155",
            lineHeight: 1.5,
          }}
        >
          <strong>
            {isFR
              ? "À préserver"
              : "Preserve"}
            :
          </strong>{" "}
          {record.continuityRule}
        </p>
      ) : null}
    </article>
  );
}

export default function ScreenplayContinuityLedger({
  projectId,
  canManage = false,
  active = false,
  isFR = false,
}) {
  const id =
    clean(projectId);

  const formRef =
    useRef(null);

  const archiveCancelRef =
    useRef(null);

  const [
    records,
    setRecords,
  ] = useState([]);

  const [
    includeArchived,
    setIncludeArchived,
  ] = useState(false);

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    typeFilter,
    setTypeFilter,
  ] = useState("all");

  const [
    visibleLimit,
    setVisibleLimit,
  ] = useState(
    PAGE_SIZE
  );

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    message,
    setMessage,
  ] = useState("");

  const [
    editing,
    setEditing,
  ] = useState(null);

  const [
    pendingArchive,
    setPendingArchive,
  ] = useState(null);

  const [
    form,
    setForm,
  ] = useState(
    EMPTY_FORM
  );

  const activeRecords =
    useMemo(
      () =>
        records.filter(
          (record) =>
            clean(
              record?.status
            ).toLowerCase() !==
            "archived"
        ),
      [records]
    );

  const archivedRecords =
    useMemo(
      () =>
        records.filter(
          (record) =>
            clean(
              record?.status
            ).toLowerCase() ===
            "archived"
        ),
      [records]
    );

  const visibleRecords =
    useMemo(() => {
      const query =
        clean(
          search
        ).toLowerCase();

      return records.filter(
        (record) => {
          const archived =
            clean(
              record?.status
            ).toLowerCase() ===
            "archived";

          if (
            archived &&
            !includeArchived
          ) {
            return false;
          }

          if (
            typeFilter !==
              "all" &&
            clean(
              record?.recordType
            ) !== typeFilter
          ) {
            return false;
          }

          if (!query) {
            return true;
          }

          return searchText(
            record
          ).includes(query);
        }
      );
    }, [
      records,
      includeArchived,
      search,
      typeFilter,
    ]);

  const listedRecords =
    useMemo(
      () =>
        visibleRecords.slice(
          0,
          visibleLimit
        ),
      [
        visibleRecords,
        visibleLimit,
      ]
    );

  const hiddenCount =
    Math.max(
      0,
      visibleRecords.length -
        listedRecords.length
    );

  useEffect(() => {
    setVisibleLimit(
      PAGE_SIZE
    );
  }, [
    search,
    typeFilter,
    includeArchived,
    id,
  ]);

  useEffect(() => {
    if (!pendingArchive) {
      return undefined;
    }

    const focusTimer =
      window.setTimeout(() => {
        archiveCancelRef
          .current
          ?.focus();
      }, 0);

    function onKeyDown(
      event
    ) {
      if (
        event.key ===
          "Escape" &&
        !saving
      ) {
        setPendingArchive(
          null
        );
      }
    }

    document.addEventListener(
      "keydown",
      onKeyDown
    );

    return () => {
      window.clearTimeout(
        focusTimer
      );

      document.removeEventListener(
        "keydown",
        onKeyDown
      );
    };
  }, [
    pendingArchive,
    saving,
  ]);

  async function loadRecords() {
    if (!id) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload =
        await api.get(
          `/api/projects/${encodeURIComponent(
            id
          )}/continuity-records?includeArchived=true`
        );

      setRecords(
        Array.isArray(
          payload
            ?.continuityRecords
        )
          ? payload
              .continuityRecords
          : []
      );
    } catch (loadError) {
      setError(
        clean(
          loadError?.message
        ) ||
          (isFR
            ? "Impossible de charger le registre de continuité."
            : "Could not load the Continuity Ledger.")
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (
      !active ||
      !id
    ) {
      return;
    }

    loadRecords();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    id,
  ]);

  function updateForm(
    key,
    value
  ) {
    setForm(
      (current) => ({
        ...current,
        [key]: value,
      })
    );
  }

  function clearForm() {
    setEditing(null);
    setForm(
      EMPTY_FORM
    );
  }

  function editRecord(
    record
  ) {
    if (!canManage) {
      return;
    }

    setEditing(
      record
    );

    setForm({
      recordType:
        RECORD_TYPES.includes(
          clean(
            record
              ?.recordType
          )
        )
          ? clean(
              record
                ?.recordType
            )
          : "other",

      title:
        record?.title || "",

      fact:
        record?.fact || "",

      timing:
        record?.timing || "",

      continuityRule:
        record
          ?.continuityRule ||
        "",

      notes:
        record?.notes || "",
    });

    setError("");
    setMessage("");

    window.setTimeout(() => {
      formRef.current
        ?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
    }, 0);
  }

  function preservedContext(
    record
  ) {
    return {
      relatedCharacterIds:
        Array.isArray(
          record
            ?.relatedCharacterIds
        )
          ? record
              .relatedCharacterIds
          : [],

      relatedLocationIds:
        Array.isArray(
          record
            ?.relatedLocationIds
        )
          ? record
              .relatedLocationIds
          : [],

      storyMapCardIds:
        Array.isArray(
          record
            ?.storyMapCardIds
        )
          ? record
              .storyMapCardIds
          : [],

      sceneIds:
        Array.isArray(
          record?.sceneIds
        )
          ? record.sceneIds
          : [],

      elementIds:
        Array.isArray(
          record
            ?.elementIds
        )
          ? record
              .elementIds
          : [],
    };
  }

  async function saveRecord(
    event
  ) {
    event.preventDefault();

    if (
      !canManage ||
      saving
    ) {
      return;
    }

    const title =
      clean(
        form.title
      );

    if (!title) {
      setError(
        isFR
          ? "Le titre de l'élément de continuité est requis."
          : "A continuity record title is required."
      );
      return;
    }

    const payload = {
      recordType:
        RECORD_TYPES.includes(
          clean(
            form.recordType
          )
        )
          ? clean(
              form.recordType
            )
          : "other",

      title,

      fact:
        clean(
          form.fact
        ),

      timing:
        clean(
          form.timing
        ),

      continuityRule:
        clean(
          form
            .continuityRule
        ),

      notes:
        clean(
          form.notes
        ),

      order:
        Number(
          editing?.order ||
          0
        ),

      ...preservedContext(
        editing
      ),
    };

    setSaving(true);
    setError("");
    setMessage("");

    try {
      if (
        editing?.id
      ) {
        await api.put(
          `/api/projects/${encodeURIComponent(
            id
          )}/continuity-records/${encodeURIComponent(
            editing.id
          )}`,
          payload
        );

        setMessage(
          isFR
            ? "Élément de continuité mis à jour."
            : "Continuity record updated."
        );
      } else {
        await api.post(
          `/api/projects/${encodeURIComponent(
            id
          )}/continuity-records`,
          payload
        );

        setMessage(
          isFR
            ? "Élément de continuité ajouté."
            : "Continuity record added."
        );
      }

      clearForm();
      await loadRecords();
    } catch (saveError) {
      setError(
        clean(
          saveError?.message
        ) ||
          (isFR
            ? "Impossible d'enregistrer cet élément de continuité."
            : "Could not save this continuity record.")
      );
    } finally {
      setSaving(false);
    }
  }

  function requestArchive(
    record
  ) {
    if (
      !canManage ||
      saving
    ) {
      return;
    }

    setError("");
    setMessage("");
    setPendingArchive(
      record
    );
  }

  async function confirmArchive() {
    if (
      !canManage ||
      !pendingArchive?.id ||
      saving
    ) {
      return;
    }

    const target =
      pendingArchive;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await api.patch(
        `/api/projects/${encodeURIComponent(
          id
        )}/continuity-records/${encodeURIComponent(
          target.id
        )}/archive`,
        {}
      );

      setPendingArchive(
        null
      );

      setMessage(
        isFR
          ? "Élément de continuité archivé."
          : "Continuity record archived."
      );

      if (
        clean(
          editing?.id
        ) ===
        clean(
          target.id
        )
      ) {
        clearForm();
      }

      await loadRecords();
    } catch (archiveError) {
      setError(
        clean(
          archiveError?.message
        ) ||
          (isFR
            ? "Impossible d'archiver cet élément de continuité."
            : "Could not archive this continuity record.")
      );
    } finally {
      setSaving(false);
    }
  }

  async function restoreRecord(
    record
  ) {
    if (
      !canManage ||
      !record?.id ||
      saving
    ) {
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await api.patch(
        `/api/projects/${encodeURIComponent(
          id
        )}/continuity-records/${encodeURIComponent(
          record.id
        )}/restore`,
        {}
      );

      setMessage(
        isFR
          ? "Élément de continuité restauré."
          : "Continuity record restored."
      );

      await loadRecords();
    } catch (restoreError) {
      setError(
        clean(
          restoreError?.message
        ) ||
          (isFR
            ? "Impossible de restaurer cet élément de continuité."
            : "Could not restore this continuity record.")
      );
    } finally {
      setSaving(false);
    }
  }

  if (
    !active ||
    !id
  ) {
    return null;
  }

  return (
    <section
      className="ns-card screenplay-continuity-ledger"
      style={{
        marginTop: 22,
      }}
    >
      <div
        style={{
          display: "grid",
          gap: 6,
        }}
      >
        <div
          className="ns-card-eyebrow"
          style={{
            color: "#0f766e",
          }}
        >
          {isFR
            ? "CONTINUITÉ"
            : "CONTINUITY"}
        </div>

        <h3
          style={{
            margin: 0,
          }}
        >
          {isFR
            ? "Registre de continuité"
            : "Continuity Ledger"}
        </h3>

        <p
          className="ns-card-meta"
          style={{
            margin: 0,
            lineHeight: 1.5,
            maxWidth: 940,
          }}
        >
          {isFR
            ? "Conservez les faits, l'ordre des événements, les objets, costumes, états physiques, documents, règles de l'histoire et autres détails qui doivent rester cohérents."
            : "Keep the facts, event order, objects, costumes, physical states, documents, story rules and other details that must remain consistent."}
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(220px, 1fr) minmax(180px, 260px) auto",
          gap: 12,
          alignItems: "end",
          marginTop: 18,
        }}
      >
        <Field
          label={
            isFR
              ? "Rechercher"
              : "Find a continuity record"
          }
        >
          <input
            className="ns-input"
            value={search}
            onChange={
              (event) =>
                setSearch(
                  event.target
                    .value
                )
            }
            placeholder={
              isFR
                ? "Titre, fait, moment, règle..."
                : "Title, fact, timing, rule..."
            }
          />
        </Field>

        <Field
          label={
            isFR
              ? "Type"
              : "Type"
          }
        >
          <select
            className="ns-input"
            value={typeFilter}
            onChange={
              (event) =>
                setTypeFilter(
                  event.target
                    .value
                )
            }
          >
            <option value="all">
              {isFR
                ? "Tous les types"
                : "All types"}
            </option>

            {RECORD_TYPES.map(
              (type) => (
                <option
                  key={type}
                  value={type}
                >
                  {typeLabel(
                    type,
                    isFR
                  )}
                </option>
              )
            )}
          </select>
        </Field>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minHeight: 42,
            whiteSpace: "nowrap",
          }}
        >
          <input
            type="checkbox"
            checked={
              includeArchived
            }
            onChange={
              (event) =>
                setIncludeArchived(
                  event.target
                    .checked
                )
            }
          />

          <span>
            {isFR
              ? "Afficher les archivés"
              : "Show archived"}
          </span>
        </label>
      </div>

      <p
        className="ns-card-meta"
        style={{
          margin:
            "12px 0 0",
        }}
      >
        {activeRecords.length}{" "}
        {isFR
          ? "actif(s)"
          : "active"}{" "}
        ·{" "}
        {archivedRecords.length}{" "}
        {isFR
          ? "archivé(s)"
          : "archived"}{" "}
        ·{" "}
        {visibleRecords.length}{" "}
        {isFR
          ? "affiché(s)"
          : "shown"}
      </p>

      {error ? (
        <div
          className="ns-alert ns-alert-error"
          style={{
            marginTop: 16,
          }}
        >
          {error}
        </div>
      ) : null}

      {message ? (
        <div
          className="ns-alert ns-alert-success"
          style={{
            marginTop: 16,
          }}
        >
          {message}
        </div>
      ) : null}

      <div
        style={{
          borderTop:
            "1px solid #e2e8f0",
          marginTop: 18,
          paddingTop: 18,
        }}
      >
        <h4
          style={{
            margin:
              "0 0 12px",
          }}
        >
          {isFR
            ? "Éléments de continuité"
            : "Continuity records"}
        </h4>

        {loading ? (
          <p
            className="ns-card-meta"
          >
            {isFR
              ? "Chargement..."
              : "Loading..."}
          </p>
        ) : listedRecords.length ===
          0 ? (
          <p
            className="ns-card-meta"
          >
            {isFR
              ? "Aucun élément de continuité ne correspond à cette vue."
              : "No continuity records match this view."}
          </p>
        ) : (
          listedRecords.map(
            (record) => (
              <ContinuityRecordCard
                key={
                  record.id
                }
                record={
                  record
                }
                isFR={
                  isFR
                }
                canManage={
                  canManage
                }
                onEdit={
                  editRecord
                }
                onArchive={
                  requestArchive
                }
                onRestore={
                  restoreRecord
                }
              />
            )
          )
        )}

        {hiddenCount > 0 ? (
          <button
            type="button"
            className="ns-button ns-button-outline"
            onClick={() =>
              setVisibleLimit(
                (current) =>
                  current +
                  PAGE_SIZE
              )
            }
          >
            {isFR
              ? `Afficher ${Math.min(
                  PAGE_SIZE,
                  hiddenCount
                )} de plus`
              : `Show ${Math.min(
                  PAGE_SIZE,
                  hiddenCount
                )} more`}
          </button>
        ) : null}
      </div>

      {!canManage ? (
        <div
          className="ns-alert"
          style={{
            marginTop: 18,
            background:
              "#f8fafc",
            borderColor:
              "#dbe4ee",
          }}
        >
          {isFR
            ? "Vous pouvez consulter le registre de continuité. Seul le propriétaire du projet peut modifier ces éléments."
            : "You can read the Continuity Ledger. Only the project Owner can change these records."}
        </div>
      ) : (
        <form
          ref={formRef}
          onSubmit={
            saveRecord
          }
          style={{
            marginTop: 20,
            padding:
              "18px 20px",
            border:
              "1px solid #dbe7df",
            borderTop:
              "2px solid #4f7968",
            borderRadius: 12,
            background:
              "#fbfdfc",
            display: "grid",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              gap: 12,
              alignItems:
                "center",
              flexWrap: "wrap",
            }}
          >
            <h4
              style={{
                margin: 0,
              }}
            >
              {editing
                ? isFR
                  ? "Modifier l'élément"
                  : "Edit continuity record"
                : isFR
                  ? "Ajouter un élément"
                  : "Add continuity record"}
            </h4>

            {editing ? (
              <span
                className="ns-card-meta"
              >
                {isFR
                  ? "Modification d'un élément existant"
                  : "Editing an existing record"}
              </span>
            ) : null}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "minmax(220px, 0.7fr) minmax(260px, 1.3fr)",
              gap: 14,
            }}
          >
            <Field
              label={
                isFR
                  ? "Type *"
                  : "Type *"
              }
            >
              <select
                className="ns-input"
                value={
                  form.recordType
                }
                onChange={
                  (event) =>
                    updateForm(
                      "recordType",
                      event.target
                        .value
                    )
                }
              >
                {RECORD_TYPES.map(
                  (type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {typeLabel(
                        type,
                        isFR
                      )}
                    </option>
                  )
                )}
              </select>
            </Field>

            <Field
              label={
                isFR
                  ? "Titre *"
                  : "Title *"
              }
              help={
                isFR
                  ? "Un nom court et reconnaissable pour ce fait ou cette règle."
                  : "A short, recognizable name for this fact or rule."
              }
            >
              <input
                className="ns-input"
                value={
                  form.title
                }
                onChange={
                  (event) =>
                    updateForm(
                      "title",
                      event.target
                        .value
                    )
                }
              />
            </Field>
          </div>

          <Field
            label={
              isFR
                ? "Fait établi"
                : "Established fact"
            }
            help={
              isFR
                ? "Ce qui est vrai dans le scénario et doit rester cohérent."
                : "What is true in the screenplay and should remain consistent."
            }
          >
            <textarea
              className="ns-input"
              rows={3}
              value={
                form.fact
              }
              onChange={
                (event) =>
                  updateForm(
                    "fact",
                    event.target
                      .value
                  )
              }
            />
          </Field>

          <Field
            label={
              isFR
                ? "Moment / ordre"
                : "Timing / sequence"
            }
            help={
              isFR
                ? "Quand cela se produit, avant ou après quoi, ou pendant quelle période."
                : "When it happens, what it comes before or after, or the period it belongs to."
            }
          >
            <textarea
              className="ns-input"
              rows={2}
              value={
                form.timing
              }
              onChange={
                (event) =>
                  updateForm(
                    "timing",
                    event.target
                      .value
                  )
              }
            />
          </Field>

          <Field
            label={
              isFR
                ? "À préserver"
                : "Continuity rule to preserve"
            }
            help={
              isFR
                ? "Ce que les scènes ultérieures, les collaborateurs et toute assistance future ne doivent pas contredire sans décision du créateur."
                : "What later scenes, collaborators and any future assistance should not contradict without a creator decision."
            }
          >
            <textarea
              className="ns-input"
              rows={3}
              value={
                form
                  .continuityRule
              }
              onChange={
                (event) =>
                  updateForm(
                    "continuityRule",
                    event.target
                      .value
                  )
              }
            />
          </Field>

          <Field
            label={
              isFR
                ? "Notes partagées"
                : "Shared notes"
            }
            help={
              isFR
                ? "Contexte supplémentaire visible par les membres actifs du projet."
                : "Additional context visible to active project members."
            }
          >
            <textarea
              className="ns-input"
              rows={3}
              value={
                form.notes
              }
              onChange={
                (event) =>
                  updateForm(
                    "notes",
                    event.target
                      .value
                  )
              }
            />
          </Field>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.45,
            }}
          >
            {isFR
              ? "Les liens vers personnages, lieux, cartes de Story Map et scènes seront ajoutés plus tard par des sélecteurs lisibles. Les identifiants internes ne sont pas exposés ici."
              : "Character, Location, Story Map and scene links will later use readable pickers. Internal record identifiers are not exposed here."}
          </p>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              type="submit"
              className="ns-button"
              disabled={
                saving
              }
            >
              {saving
                ? isFR
                  ? "Enregistrement..."
                  : "Saving..."
                : editing
                  ? isFR
                    ? "Enregistrer les modifications"
                    : "Save changes"
                  : isFR
                    ? "Ajouter l'élément"
                    : "Add continuity record"}
            </button>

            <button
              type="button"
              className="ns-button ns-button-outline"
              disabled={
                saving
              }
              onClick={
                clearForm
              }
            >
              {isFR
                ? "Effacer le formulaire"
                : "Clear form"}
            </button>
          </div>
        </form>
      )}

      <div
        style={{
          marginTop: 18,
          padding:
            "14px 16px",
          borderRadius: 10,
          background:
            "#f8fafc",
          color:
            "#475569",
          lineHeight: 1.5,
        }}
      >
        <strong>
          {isFR
            ? "Continuité d'abord, automatisation plus tard"
            : "Continuity first, automation later"}
        </strong>

        <div
          style={{
            marginTop: 4,
          }}
        >
          {isFR
            ? "Ces éléments constituent une mémoire de projet contrôlée par le créateur. L'IA reste consultative : elle ne crée, ne modifie, n'adopte ni ne transforme automatiquement ces éléments en Canon."
            : "These records are creator-controlled project memory. AI remains advisory: it does not create, change, adopt or automatically turn these records into Canon."}
        </div>
      </div>

      {pendingArchive ? (
        <div
          className="screenplay-continuity-archive-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(15,23,42,0.56)",
            display: "grid",
            placeItems:
              "center",
            zIndex: 10000,
            padding: 20,
          }}
          onMouseDown={
            (event) => {
              if (
                event.target ===
                  event.currentTarget &&
                !saving
              ) {
                setPendingArchive(
                  null
                );
              }
            }
          }
        >
          <div
            className="screenplay-continuity-archive-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="screenplay-continuity-archive-title"
            aria-describedby="screenplay-continuity-archive-copy"
            style={{
              width:
                "min(520px, 100%)",
              background:
                "#ffffff",
              borderRadius: 18,
              padding:
                "24px 28px",
              boxShadow:
                "0 24px 60px rgba(15,23,42,0.30)",
            }}
          >
            <div
              style={{
                fontSize: 13,
                letterSpacing:
                  "0.08em",
                fontWeight: 800,
                color:
                  "#b45309",
                marginBottom: 8,
              }}
            >
              {isFR
                ? "ARCHIVER L'ÉLÉMENT"
                : "ARCHIVE CONTINUITY RECORD"}
            </div>

            <h3
              id="screenplay-continuity-archive-title"
              style={{
                margin:
                  "0 0 10px",
              }}
            >
              {isFR
                ? `Archiver ${pendingArchive.title || "cet élément"} ?`
                : `Archive ${pendingArchive.title || "this record"}?`}
            </h3>

            <p
              id="screenplay-continuity-archive-copy"
              style={{
                margin:
                  "0 0 20px",
                color:
                  "#475569",
                lineHeight: 1.5,
              }}
            >
              {isFR
                ? "Cet élément quittera la liste active. Il ne sera pas supprimé définitivement et pourra être restauré plus tard."
                : "This record will move out of the active list. It will not be permanently deleted and can be restored later."}
            </p>

            <div
              style={{
                display:
                  "flex",
                justifyContent:
                  "flex-end",
                gap: 10,
                flexWrap:
                  "wrap",
              }}
            >
              <button
                ref={
                  archiveCancelRef
                }
                type="button"
                className="ns-button ns-button-outline"
                disabled={
                  saving
                }
                onClick={() =>
                  setPendingArchive(
                    null
                  )
                }
              >
                {isFR
                  ? "Annuler"
                  : "Cancel"}
              </button>

              <button
                type="button"
                className="ns-button"
                disabled={
                  saving
                }
                onClick={
                  confirmArchive
                }
                style={{
                  background:
                    "#b45309",
                  borderColor:
                    "#b45309",
                }}
              >
                {saving
                  ? isFR
                    ? "Archivage..."
                    : "Archiving..."
                  : isFR
                    ? "Archiver l'élément"
                    : "Archive record"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
